import React, { Suspense, useState, useEffect, useMemo, useCallback, useRef } from "react";
import axios from "axios";
import { Browser } from "@capacitor/browser";
import { Capacitor } from "@capacitor/core";
import { CalendarDays, Home, Plus, RefreshCw, Route, UserRound } from "lucide-react";
import AdminTabs, { AdminBreadcrumb } from "../components/admin/AdminTabs";
import AdminDashboardContent from "../components/admin/AdminDashboardContent";
import { serviceDateFetchParams } from '../utils/orderServiceDateRange';
import { SettlementFormModal } from "../components/admin/AdminSettlements";
import useAdminBilling from "../hooks/admin/useAdminBilling";
import useAdminBillingDocuments from "../hooks/admin/useAdminBillingDocuments";
import useAdminDispatchers from "../hooks/admin/useAdminDispatchers";
import useAdminDrivers from "../hooks/admin/useAdminDrivers";
import useAdminOrderActions from "../hooks/admin/useAdminOrderActions";
import useAdminOrderDisplay from "../hooks/admin/useAdminOrderDisplay";
import useAdminOrderEditing from "../hooks/admin/useAdminOrderEditing";
import useAdminOrders from "../hooks/admin/useAdminOrders";
import useAdminPricingConfig from "../hooks/admin/useAdminPricingConfig";
import useAdminQuotes from "../hooks/admin/useAdminQuotes";
import useAdminStats from "../hooks/admin/useAdminStats";
import useAdminSettlements from "../hooks/admin/useAdminSettlements";
import useAdminUsers from "../hooks/admin/useAdminUsers";
import useCustomerPricingModal from "../hooks/admin/useCustomerPricingModal";
import useFrontendLayoutConfig from "../hooks/admin/useFrontendLayoutConfig";
import { API_BASE_URL as API, BACKEND_URL } from "../apiConfig";
import { buildDriverTimeOffDateRange } from "../utils/driverTimeOffDisplay";
import { addDaysToDateKey, getTokyoDateKey } from "../utils/tokyoDate";
import shonanPalmLogo from "../assets/shonan-palm-gold-transparent.png";

const AdminDashboardModals = React.lazy(() => import("../components/admin/AdminDashboardModals"));
const ADMIN_DEFAULT_ORDER_LOOKBACK_DAYS = 30;
const ADMIN_ORDER_RANGE_HELP_TEXT = "默认显示近30天及未来订单，旧订单可用搜索框查询";
const ADMIN_MOBILE_DISPATCH_DRIVER_SELECTION_KEY = "admin_mobile_dispatch_selected_driver_ids";

function formatDateParam(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function getDefaultOrderDateFrom() {
  const date = new Date();
  date.setDate(date.getDate() - ADMIN_DEFAULT_ORDER_LOOKBACK_DAYS);
  return formatDateParam(date);
}

function getAdminOrderFetchParams({ isAdminAppMode, monthFilter }) {
  if (isAdminAppMode) {
    return {
      month: undefined,
      date_from: getDefaultOrderDateFrom(),
    };
  }

  return {
    month: monthFilter || undefined,
    date_from: monthFilter ? undefined : getDefaultOrderDateFrom(),
  };
}

function readAdminMobileDispatchDriverSelection() {
  try {
    const savedValue = localStorage.getItem(ADMIN_MOBILE_DISPATCH_DRIVER_SELECTION_KEY);
    if (savedValue === null) return [];
    const parsedValue = JSON.parse(savedValue);
    return Array.isArray(parsedValue) ? parsedValue.filter(Boolean) : [];
  } catch (error) {
    return [];
  }
}

function hasStoredAdminMobileDispatchDriverSelection() {
  try {
    return localStorage.getItem(ADMIN_MOBILE_DISPATCH_DRIVER_SELECTION_KEY) !== null;
  } catch (error) {
    return false;
  }
}

export default function AdminDashboard({ user, onLogout, appMode = "web" }) {
  const isAdminAppMode = appMode === "admin-app";
  const isFinanceMode = user?.role === "finance";
  const [activeTab, setActiveTab] = useState(() => (
    isFinanceMode?"settlements":isAdminAppMode?(user?.is_master||user?.permissions?.includes("orders.view")?"mobileHome":"mobileProfile"):(user?.is_master?"users":user?.permissions?.includes("orders.view")?"orders":user?.permissions?.includes("prices.view")?"pricing":user?.permissions?.includes("finance.view")?"settlements":user?.permissions?.includes("dispatch.manage")?"drivers":"audit")
  ));
  const [communicationOrderId, setCommunicationOrderId] = useState("");
  const [communicationRequestNonce, setCommunicationRequestNonce] = useState(0);
  const [adminMobileOrderDetail, setAdminMobileOrderDetail] = useState(null);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [driverTimeOffByDate, setDriverTimeOffByDate] = useState({});
  const [mobileDispatchExtraSchedules, setMobileDispatchExtraSchedules] = useState([]);
  const [mobileDispatchExtraSchedulesLoading, setMobileDispatchExtraSchedulesLoading] = useState(false);
  const [mobileDispatchExtraSchedulesError, setMobileDispatchExtraSchedulesError] = useState("");
  const [mobileDispatchSelectedDriverIds, setMobileDispatchSelectedDriverIds] = useState(() => readAdminMobileDispatchDriverSelection());
  const [mobileDispatchSelectionInitialized, setMobileDispatchSelectionInitialized] = useState(() => hasStoredAdminMobileDispatchDriverSelection());
  const [mobileOrderInitialQuickView, setMobileOrderInitialQuickView] = useState("");
  const [adminAppNotice, setAdminAppNotice] = useState("");

  const notifyAdminApp = (message) => {
    if (!message) return;
    setAdminAppNotice(message);
    window.clearTimeout(notifyAdminApp.timeoutId);
    notifyAdminApp.timeoutId = window.setTimeout(() => setAdminAppNotice(""), 2200);
  };

  const updateMobileDispatchSelectedDriverIds = useCallback((nextValue) => {
    setMobileDispatchSelectedDriverIds((current) => {
      const resolvedValue = typeof nextValue === "function" ? nextValue(current) : nextValue;
      const nextDriverIds = Array.isArray(resolvedValue) ? resolvedValue.filter(Boolean) : [];
      try {
        localStorage.setItem(ADMIN_MOBILE_DISPATCH_DRIVER_SELECTION_KEY, JSON.stringify(nextDriverIds));
      } catch (error) {
        console.warn("保存调度司机筛选失败:", error);
      }
      return nextDriverIds;
    });
  }, []);

  // Admin create order for customer
  const [showAdminOrderForm, setShowAdminOrderForm] = useState(false);
  const [duplicateOrderPrefill, setDuplicateOrderPrefill] = useState(null);

  // Helper function to extract error message from API response
  const getErrorMessage = (error, defaultMessage = "操作失败") => {
    const detail = error.response?.data?.detail;
    if (typeof detail === 'string') {
      return detail;
    } else if (typeof detail === 'object' && detail !== null) {
      return detail.message_zh || detail.message || detail.error || defaultMessage;
    } else if (error.message) {
      return error.message;
    }
    return defaultMessage;
  };

  const formatLocalDateKey = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const expandDriverTimeOffByDate = (items) => {
    const grouped = {};
    items.forEach((item) => {
      const start = new Date(`${item.start_date}T00:00:00`);
      const end = new Date(`${item.end_date || item.start_date}T00:00:00`);
      for (let current = new Date(start); current <= end; current.setDate(current.getDate() + 1)) {
        const key = formatLocalDateKey(current);
        if (!grouped[key]) grouped[key] = [];
        grouped[key].push(item);
      }
    });
    return grouped;
  };

  const fetchDriverTimeOff = async (sourceOrders = orders) => {
    const range = buildDriverTimeOffDateRange({ orders: sourceOrders, monthFilter });
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/admin/driver-time-off`, {
        params: range,
        headers: { Authorization: `Bearer ${token}` },
      });
      const items = response.data || [];
      setDriverTimeOffByDate(expandDriverTimeOffByDate(items));
    } catch (error) {
      console.error("获取司机休假失败:", error);
      setDriverTimeOffByDate({});
    }
  };

  const fetchMobileDispatchExtraSchedules = async () => {
    const dateFrom = getTokyoDateKey();
    const dateTo = addDaysToDateKey(dateFrom, 2);
    setMobileDispatchExtraSchedulesLoading(true);
    setMobileDispatchExtraSchedulesError("");
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/admin/extra-schedule/list`, {
        params: {
          date_from: dateFrom,
          date_to: dateTo,
          status: "active",
        },
        headers: { Authorization: `Bearer ${token}` },
      });
      setMobileDispatchExtraSchedules(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error("获取调度追加预约失败:", error);
      setMobileDispatchExtraSchedules([]);
      setMobileDispatchExtraSchedulesError("调度预约加载失败，请刷新重试");
    } finally {
      setMobileDispatchExtraSchedulesLoading(false);
    }
  };

  const fetchMobileDispatchDriverHistory = async (driverId) => {
    if (!driverId) throw new Error("司机信息不存在");
    const token = localStorage.getItem("token");
    const response = await axios.get(`${API}/admin/extra-schedule/driver/${driverId}/history`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  };

  const {
    allUsers,
    closeResetPasswordForm,
    closeUserProfileForm,
    fetchAllUsers,
    fetchPendingUsers,
    handleApproveUser,
    handleBatchDeleteUsers,
    handleCreateUser,
    handleResetPassword,
    handleUpdateUserProfile,
    handleUpdateUserChatSettings,
    handleUpdateUserRetailMemberBindingSettings,
    handleUpdateUserStatus,
    newPasswordForm,
    newUserForm,
    openResetPasswordForm,
    openUserProfileForm,
    pendingUsers,
    selectedUserForAction,
    selectedUserIds,
    setNewPasswordForm,
    setNewUserForm,
    setUserProfileForm,
    setShowCreateUserForm,
    setShowDeletedUsers,
    showCreateUserForm,
    showDeletedUsers,
    showResetPasswordForm,
    showUserProfileForm,
    toggleAllUsers,
    toggleUserSelection,
    userProfileForm,
  } = useAdminUsers({
    apiBaseUrl: API,
    getErrorMessage,
    setLoading,
  });

  const {
    closeCustomerStats,
    dashboardStats,
    fetchCustomerStats,
    fetchDashboardStats,
    fetchMonthlyTrend,
    fetchRecentOrders,
    monthlyTrend,
    recentOrders,
    selectedCustomerStats,
    statsLoading,
  } = useAdminStats({ apiBaseUrl: API });

  const refreshStatsAfterOrderChange = useCallback(() => {
    fetchDashboardStats();
    fetchMonthlyTrend();
    fetchRecentOrders();
  }, [fetchDashboardStats, fetchMonthlyTrend, fetchRecentOrders]);

  const {
    clearSearch,
    cancelRetailBooking,
    confirmRetailBooking,
    cycleDateSort,
    dateSortDirection,
    customerFilter,
    filteredOrders,
    getCharterTimeStatus,
    getOrderTimeCategory,
    getStatusBadge,
    getTimeCategoryBadge,
    getUniqueCustomers,
    getVehicleTypeName,
    isMissingDriverOrder,
    generateBookingPaymentPage,
    groupOrdersByDate,
    handleBulkDelete,
    handleBulkMarkPaid,
    isMarkingPaid,
    handleApplyCustomerOvertimeCharge,
    handleMarkOvertimeHandled,
    handleSearchKeyDown,
    handleSearchOrders,
    refreshSearchResults,
    hideCancelled,
    hideCompleted,
    isSearchMode,
    isSearching,
    monthFilter,
    serviceDateRange,
    setServiceDateRange,
    paymentCopyPanel,
    orderStatusFilter,
    resetFilters,
    searchKeyword,
    searchMessage,
    searchResults,
    selectAll,
    selectedOrderIds,
    setCustomerFilter,
    setHideCancelled,
    setHideCompleted,
    setMonthFilter,
    setOrderStatusFilter,
    setSearchKeyword,
    sortByDate,
    toggleUnlinkedCustomerOnly,
    toggleSelectAll,
    toggleSelectOrder,
    selectOrderGroup,
    unlinkedCustomerOnly,
    closePaymentCopyPanel,
    retryCopyPaymentMessage,
    openPaymentMessageWhatsApp,
    sharePaymentMessage,
  } = useAdminOrders({
    apiBaseUrl: API,
    orders,
    fetchOrders: () => fetchOrders(),
    getErrorMessage,
    refreshStats: refreshStatsAfterOrderChange,
    setLoading,
    notifyAdminApp: isAdminAppMode ? notifyAdminApp : undefined,
  });

  const {
    billingFilters,
    handleExportBilling,
    setBillingFilters,
  } = useAdminBilling({
    apiBaseUrl: API,
    setLoading,
  });

  const {
    billingDocumentFilters,
    billingDocumentSummary,
    billingDocuments,
    billingDocumentsLoading,
    fetchBillingDocuments,
    fetchReceiptDocuments,
    handleBillingDocumentFiltersChange,
    handleCreateCombinedOrderReceipt,
    handleCreateManualInvoice,
    handleCreateManualReceipt,
    handleDeleteBillingDocument,
    handleDownloadBillingDocument,
    handleExportBillingDocuments,
    handleReceiptDocumentFiltersChange,
    handleRestoreBillingDocument,
    handleSearchReceiptOrders,
    handleCreateSingleOrderReceipts,
    receiptDocumentFilters,
    receiptDocumentSummary,
    receiptDocuments,
    receiptDocumentsLoading,
  } = useAdminBillingDocuments({
    apiBaseUrl: API,
    setLoading,
  });

  const {
    areaNameMap,
    areas,
    charterPricing,
    extraHoursConfig,
    fetchAreas,
    fetchPricingConfig,
    importPresetData,
    initializeAreas,
    nightSurcharges,
    overtimeRates,
    pricingTab,
    selectedAreas,
    setEditingItem,
    setExtraHoursConfig,
    setPricingTab,
    setShowAreaForm,
    setShowBatchAreaForm,
    setShowSurchargeForm,
    toggleAllAreas,
    toggleAreaSelection,
    transferPricing,
  } = useAdminPricingConfig({
    apiBaseUrl: API,
    setLoading,
  });

  const {
    formatDestinationDisplay,
    formatPickupDisplay,
    getAvailableVehicleTypes,
    isKansaiRegion,
    isOrderIncomplete,
  } = useAdminOrderDisplay({
    areas,
    areaNameMap,
  });

  const {
    adminEditExtraServices,
    adminEditExtendedWaitingQuantity,
    adminEditOtherServiceDesc,
    adminEditParkingFee,
    apiCalculatedPrice,
    apiPriceLoading,
    closeOrderEditForm,
    editingOrder,
    getAdminCalculatedPrice,
    getAdminChildSeatPrice,
    getAdminExtraServiceTotal,
    getAdminVehiclePrice,
    handleEditOrder,
    handleRecalculateLatestPrice,
    handleRequestSquareRefund,
    handleUpdateOrder,
    orderEditForm,
    setAdminEditExtraServices,
    setAdminEditExtendedWaitingQuantity,
    setAdminEditOtherServiceDesc,
    setAdminEditParkingFee,
    setOrderEditForm,
    showOrderEditForm,
  } = useAdminOrderEditing({
    apiBaseUrl: API,
    backendUrl: BACKEND_URL,
    charterPricing,
    extraHoursConfig,
    fetchOrders: () => fetchOrders(),
    getErrorMessage,
    refreshStats: refreshStatsAfterOrderChange,
    setLoading,
    transferPricing,
  });

  const {
    closeCustomerPricing,
    openCustomerPricing,
    selectedCustomerForPricing,
    showCustomerPricingForm,
  } = useCustomerPricingModal();

  const {
    closeQuoteForm,
    handleCreateQuote,
    openQuoteForm,
    quoteForm,
    selectedOrderForQuote,
    setQuoteForm,
    showQuoteForm,
  } = useAdminQuotes({
    apiBaseUrl: API,
    fetchOrders: () => fetchOrders(),
    getErrorMessage,
    refreshStats: refreshStatsAfterOrderChange,
    setLoading,
  });

  const {
    addAreaToRow,
    addLayoutRow,
    fetchLayoutConfig,
    layoutConfig,
    layoutLoading,
    moveAreaDown,
    moveAreaUp,
    removeAreaFromRow,
    removeLayoutRow,
    saveLayoutConfig,
  } = useFrontendLayoutConfig({ apiBaseUrl: API });

  const {
    closeDriverAccountForm,
    closeDriverComplaintForm,
    closeDriverSchedule,
    closeOrderPoolForm,
    driverAccountForm,
    driverAccounts,
    driverAccountsError,
    driverAccountsLoading,
    driverComplaintError,
    driverComplaintForm,
    driverComplaintOptions,
    driverComplaintOptionsLoading,
    driverComplaintSubmitting,
    driverExtraSchedules,
    driverMonthlyStats,
    driverMonthlyStatsDispatcherId,
    driverMonthlyStatsDriverId,
    driverMonthlyStatsLoading,
    driverMonthlyStatsMonth,
    driverNoticeForm,
    driverNotices,
    driverNoticeTranslating,
    driverOnboardingLoading,
    driverTrainingConfig,
    driverForm,
    driverScheduleLoading,
    driverScheduleOrders,
    editingDriverAccount,
    editingDriverNotice,
    fetchDriverAccounts,
    fetchDriverNotices,
    fetchDriverMonthlyStats,
    fetchDriverSchedule,
    fetchDriverOnboarding,
    fetchSavedDrivers,
    handleAssignDriverToOrder,
    handleCancelExtraSchedule,
    handleCreateDriverAccount,
    handleDeleteDriver,
    handleDeleteDriverAccount,
    handleDriverMonthlyStatsDispatcherChange,
    handleDriverMonthlyStatsDriverChange,
    handleDriverMonthlyStatsMonthChange,
    handleSubmitDriverMonthlySettlements,
    handleUpdateDriverMonthlyDispatchPrice,
    handleExportDriverMonthlyStats,
    handleMigrateDriverOrders,
    handleNotifyAssignedDriver,
    handlePutOrderIntoPool,
    handleSubmitOrderPoolForm,
    handleWithdrawOrderFromPool,
    handleCreateDriverComplaint,
    handleDriverComplaintIssueToggle,
    handleSubmitDriverComplaintForm,
    handleResolveDriverIssue,
    handleReviewDriverOnboarding,
    handleEditDriverNotice,
    handleSaveDriverNotice,
    handleTranslateDriverNotice,
    handleSaveDriverTrainingConfig,
    handleResetDriverPassword,
    handleSaveDriver,
    handleToggleDriverActive,
    handleToggleDriverInternalDispatchOverview,
    handleToggleDriverPinned,
    handleToggleDriverRevenueStats,
    handleToggleFavoriteDriver,
    handleUnassignDriver,
    handleUpdateDriverAccount,
    hideCompletedDriverOrders,
    openCreateDriverForm,
    openDriverAssignModal,
    openEditDriverForm,
    savedDrivers,
    selectedDriverForSchedule,
    selectedDriverOnboarding,
    selectedOrderForComplaint,
    selectedOrderForDriver,
    selectedOrderForPool,
    setDriverAccountForm,
    setDriverComplaintForm,
    setDriverForm,
    setHideCompletedDriverOrders,
    setSelectedDriverOnboarding,
    setDriverNoticeForm,
    setDriverTrainingConfig,
    setShowDriverTrainingConfig,
    setShowDriverNoticeModal,
    setShowDriverAssignModal,
    showDriverAccountForm,
    showDriverComplaintForm,
    showDriverNoticeModal,
    showDriverTrainingConfig,
    showDriverAssignModal,
    showDriverSchedule,
    showOrderPoolForm,
    orderPoolForm,
    orderPoolSubmitting,
    orderPoolError,
    setOrderPoolForm,
    fetchDriverTrainingConfig,
  } = useAdminDrivers({
    apiBaseUrl: API,
    fetchOrders: () => fetchOrders(),
    getErrorMessage,
    notifyAdminApp: isAdminAppMode ? notifyAdminApp : undefined,
    refreshStats: refreshStatsAfterOrderChange,
  });

  const {
    closeDispatcherForm,
    dispatcherForm,
    dispatchers,
    dispatchersLoading,
    editingDispatcher,
    fetchDispatchers,
    handleCreateDispatcher,
    handleDeleteDispatcher,
    handleToggleDispatcherActive,
    handleUpdateDispatcher,
    openCreateDispatcherForm,
    openEditDispatcherForm,
    setDispatcherForm,
    showDispatcherForm,
  } = useAdminDispatchers({ apiBaseUrl: API });

  const openAdminOrderForm = (initialForm = null) => {
    fetchAreas();
    setDuplicateOrderPrefill(initialForm);
    setShowAdminOrderForm(true);
  };

  const openDuplicateOrderForm = (order) => openAdminOrderForm({
    customer_name: order.customer_name || "",
    phone: order.phone || "",
    service_type: order.service_type === "charter" ? "charter" : "transfer",
    pickup_area: order.pickup_area || "",
    dropoff_area: order.dropoff_area || "",
    pickup_terminal: order.pickup_terminal || "",
    dropoff_terminal: order.dropoff_terminal || "",
    pickup_location_detail: order.pickup_location_detail || order.pickup_location || "",
    dropoff_location_detail: order.dropoff_location_detail || order.destination || "",
    end_location: order.end_location || "",
    end_location_in_base: order.end_location_in_base !== false,
    end_location_other: order.end_location_other || "",
  });

  const openAdminOrderAppPage = () => {
    fetchAreas();
    setShowAdminOrderForm(false);
    setActiveTab("mobileCreate");
  };

  const hasOpenAdminModal = useMemo(() => Boolean(
    showAdminOrderForm ||
    showQuoteForm ||
    showCustomerPricingForm ||
    showOrderEditForm ||
    showCreateUserForm ||
    showResetPasswordForm ||
    showUserProfileForm ||
    showDriverAssignModal ||
    showOrderPoolForm ||
    showDriverComplaintForm ||
    paymentCopyPanel ||
    showDispatcherForm ||
    showDriverSchedule
  ), [
    showAdminOrderForm,
    showQuoteForm,
    showCustomerPricingForm,
    showOrderEditForm,
    showCreateUserForm,
    showResetPasswordForm,
    showUserProfileForm,
    showDriverAssignModal,
    showOrderPoolForm,
    showDriverComplaintForm,
    paymentCopyPanel,
    showDispatcherForm,
    showDriverSchedule,
  ]);

  useEffect(() => {
    if (showOrderEditForm) fetchAreas();
    // Editors opened from the mobile home/dispatch tabs also need region options.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showOrderEditForm]);

  useEffect(() => {
    if (activeTab === "orders" || activeTab === "mobileHome" || activeTab === "mobileDispatch") {
      fetchDriverTimeOff(orders);
    }
  // Refresh driver time-off when the visible order month changes, including dates with no orders.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, monthFilter]);

  useEffect(() => {
    if (activeTab === "mobileHome") {
      fetchOrders();
      fetchSavedDrivers();
      fetchDriverAccounts();
      fetchPendingUsers();
      fetchAllUsers();
    } else if (activeTab === "mobileDispatch") {
      fetchOrders();
      fetchDriverAccounts();
      fetchMobileDispatchExtraSchedules();
    } else if (activeTab === "users") {
      fetchPendingUsers();
      fetchAllUsers();
    } else if (activeTab === "orders") {
      fetchOrders();
      fetchPricingConfig(); // Also fetch pricing config for V2 order price calculation
      fetchSavedDrivers(); // Fetch saved drivers list
      fetchDriverAccounts(); // Fetch driver accounts for assignment
      fetchAreas(); // Fetch areas for admin create order modal
      fetchAllUsers(); // Need travel agency accounts for the admin app order filter
    } else if (activeTab === "mobileCreate") {
      fetchAreas();
      fetchAllUsers();
    } else if (activeTab === "communications") {
      fetchOrders();
    } else if (activeTab === "billing") {
      fetchAllUsers(); // Need user list for billing export
    } else if (activeTab === "billingDocuments") {
      fetchBillingDocuments();
      fetchReceiptDocuments();
    } else if (activeTab === "pricing") {
      fetchPricingConfig();
    } else if (activeTab === "layout") {
      fetchLayoutConfig();
      fetchPricingConfig(); // Need areas list
    } else if (activeTab === "daily") {
      fetchRecentOrders();
    } else if (activeTab === "stats") {
      fetchDashboardStats();
      fetchMonthlyTrend();
    } else if (activeTab === "drivers") {
      fetchDriverAccounts();
      fetchDispatchers();
      fetchDriverMonthlyStats();
    } else if (activeTab === "admins") {
      fetchDispatchers();
      fetchDriverAccounts(); // Need driver list for assignment
    }
  // Load tab-specific data only when the active admin section changes.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, showDeletedUsers]);

  useEffect(() => {
    if (activeTab === "orders" || activeTab === "mobileHome") {
      fetchOrders();
    }
  // Refetch order list when the visible month changes. Mobile defaults to current month.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [monthFilter, serviceDateRange.from, serviceDateRange.to]);

  const orderFetchSequence = useRef(0);
  const fetchOrders = async () => {
    const sequence = ++orderFetchSequence.current;
    try {
      const response = await axios.get(`${API}/orders`, {
        params: { ...getAdminOrderFetchParams({ isAdminAppMode, monthFilter }), ...(!isAdminAppMode ? serviceDateFetchParams(serviceDateRange) : {}) },
      });
      if (sequence !== orderFetchSequence.current) return;
      setOrders(response.data);
      fetchDriverTimeOff(response.data || []);
      await refreshSearchResults(response.data || []);
    } catch (error) {
      console.error("获取订单失败:", error);
    }
  };

  const liveOrderRefresh = useRef(fetchOrders);
  liveOrderRefresh.current = fetchOrders;
  useEffect(() => {
    if (!["orders", "mobileHome", "mobileDispatch"].includes(activeTab)) return undefined;
    let refreshing = false;
    const refresh = async () => {
      if (document.visibilityState === "hidden" || refreshing) return;
      refreshing = true;
      try { await liveOrderRefresh.current(); } finally { refreshing = false; }
    };
    const timer = window.setInterval(refresh, 30000);
    document.addEventListener("visibilitychange", refresh);
    window.addEventListener("focus", refresh);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", refresh);
      window.removeEventListener("focus", refresh);
    };
  }, [activeTab]);

  const settlementProps = useAdminSettlements({
    apiBaseUrl: API,
    activeTab,
    user,
    onOrderRefresh: fetchOrders,
  });

  const refreshAdminMobileData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchOrders(),
        fetchSavedDrivers(),
        fetchDriverAccounts(),
        fetchPendingUsers(),
        fetchAllUsers(),
        fetchDriverTimeOff(),
      ]);
    } finally {
      setLoading(false);
    }
  };

  const refreshAdminMobileDispatchData = async () => {
    await Promise.all([
      fetchOrders(),
      fetchDriverAccounts(),
      fetchMobileDispatchExtraSchedules(),
    ]);
  };

  const getSignboardPassengerName = (order) => {
    return (order.customer_name || "Passenger").replace(/\s*\([^)]*\)\s*$/, "").trim() || "Passenger";
  };

  const getSignboardFilename = (order) => {
    const safeName = getSignboardPassengerName(order)
      .replace(/[\\/:*?"<>|\r\n\t]+/g, "_")
      .replace(/\s+/g, " ")
      .trim() || "Passenger";
    return `Signboard_${safeName}.pdf`;
  };

  const getDownloadFilename = (response, fallbackName) => {
    const disposition = response.headers?.["content-disposition"] || "";
    const encodedMatch = disposition.match(/filename\*=UTF-8''([^;]+)/i);
    if (encodedMatch) {
      return decodeURIComponent(encodedMatch[1]);
    }
    return fallbackName;
  };

  const triggerBlobDownload = (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  const resolveAdminDownloadUrl = (downloadUrl) => {
    if (!downloadUrl) return "";
    if (downloadUrl.startsWith("http")) return downloadUrl;
    const apiBase = new URL(API, window.location.origin);
    return new URL(downloadUrl, apiBase.origin).toString();
  };

  const openAdminSignboardDownloadUrl = async (downloadUrl) => {
    const resolvedUrl = resolveAdminDownloadUrl(downloadUrl);
    if (!resolvedUrl) return;
    if (Capacitor.isNativePlatform()) {
      await Browser.open({ url: resolvedUrl });
      return;
    }
    window.location.assign(resolvedUrl);
  };

  const createAdminSignboardPdfDownloadLink = async (order) => {
    const token = localStorage.getItem("token");
    const response = await axios.post(`${API}/admin/orders/${order.id}/signboard-pdf-link`, {}, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  };

  const handleDownloadSignboard = async (order) => {
    setLoading(true);
    try {
      if (isAdminAppMode) {
        const result = await createAdminSignboardPdfDownloadLink(order);
        await openAdminSignboardDownloadUrl(result.download_url);
      } else {
        const token = localStorage.getItem("token");
        const response = await axios.get(`${API}/admin/orders/${order.id}/signboard-pdf`, {
          responseType: "blob",
          headers: { Authorization: `Bearer ${token}` },
        });
        const filename = getDownloadFilename(response, getSignboardFilename(order));
        triggerBlobDownload(response.data, filename);
      }
    } catch (error) {
      alert(getErrorMessage(error, "生成举牌 PDF 失败"));
    } finally {
      setLoading(false);
    }
  };

  const {
    handleDeleteOrder,
    handleDuplicateOrder,
    handleOpenRecentOrder,
    handleRestoreOrder,
    handleUnlockOrder,
    handleViewDriverScheduleOrder,
  } = useAdminOrderActions({
    apiBaseUrl: API,
    closeDriverSchedule,
    fetchOrders,
    getErrorMessage,
    handleEditOrder,
    orders,
    openDuplicateOrderForm,
    refreshStats: refreshStatsAfterOrderChange,
    setActiveTab,
    setLoading,
  });

  const openTripCommunication = (order) => {
    if (!order?.id) return;
    setCommunicationOrderId(order.id);
    setCommunicationRequestNonce((current) => current + 1);
    setActiveTab("communications");
  };

  const openAdminMobileOrderDetail = (order) => {
    if (!order) return;
    setAdminMobileOrderDetail(order);
    setActiveTab("mobileOrderDetail");
  };

  const openAdminMobileDriverReports = () => {
    setMobileOrderInitialQuickView("driver_report_pending");
    setActiveTab("orders");
  };

  const openOperationsOrder = async (order) => {
    const keyword = String(order?.order_no || order?.confirmation_code || "").trim();
    if (!keyword) return;
    setActiveTab("orders");
    setSearchKeyword(keyword);
    await handleSearchOrders(keyword);
    window.requestAnimationFrame(() => {
      document.querySelector('[data-testid="admin-order-results"]')?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    });
  };


  return (
    <div className={`${isAdminAppMode ? "admin-mobile-portal admin-mobile-app-mode" : "admin-luxury-portal"} ${!isAdminAppMode && !isFinanceMode ? "admin-web-shell" : ""} min-h-screen premium-shell`}>
      {/* Header */}
      {!isAdminAppMode && !isFinanceMode && (
      <div className="premium-hero admin-web-header">
          <div className="admin-web-header-inner">
            <div className="shonan-compact-brand">
              <div className="shonan-compact-brand-mark">
                <img
                  src={shonanPalmLogo}
                  alt="Shonan International"
                  className="h-full w-full object-contain"
                />
              </div>
              <div className="shonan-compact-brand-copy">
                <p className="shonan-compact-brand-title">SHONAN INTERNATIONAL</p>
                <p className="shonan-compact-brand-subtitle">Private Transfer & Charter</p>
              </div>
            </div>
            <div className="admin-web-header-actions">
              <a
                href="/internal"
                className="premium-button border border-primary/30 bg-primary/10 text-sm text-primary hover:bg-primary/15"
                data-testid="admin-internal-entry-link"
              >
                车辆司机管理后台
              </a>
              <div className="admin-web-account"><UserRound size={22} aria-hidden="true" /><div>
                <strong>{user?.name || user?.phone || "管理员"}</strong><small>{user?.is_master ? "系统管理员" : "管理员"}</small>
              </div></div>
              <button
                onClick={onLogout}
                className="premium-button border border-border-subtle bg-background text-sm hover:bg-secondary"
              >
                退出登录
              </button>
            </div>
          </div>
      </div>
      )}

      {/* Tabs */}
      {!isAdminAppMode && !isFinanceMode && (
        <AdminTabs
        activeTab={activeTab}
        onTabChange={setActiveTab}
        pendingUsersCount={pendingUsers.length}
        user={user}
      />
      )}

      <div className={isAdminAppMode ? "admin-mobile-content" : isFinanceMode ? "max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-6 pb-12" : "admin-web-content"}>
        {!isAdminAppMode && !isFinanceMode && <AdminBreadcrumb activeTab={activeTab} />}
        <AdminDashboardContent
          apiBaseUrl={API}
          onUsersChanged={fetchAllUsers}
          settlementProps={settlementProps}
          user={user}
          {...{
            activeTab, allUsers, billingFilters, dashboardStats, dispatchers, dispatchersLoading,
            isAdminAppMode,
            billingDocumentFilters, billingDocumentSummary, billingDocuments, billingDocumentsLoading,
            receiptDocumentFilters, receiptDocumentSummary, receiptDocuments, receiptDocumentsLoading,
            driverAccountForm, driverAccounts, driverAccountsError, driverAccountsLoading, fetchDriverAccounts, driverMonthlyStats, driverMonthlyStatsDispatcherId,
            driverMonthlyStatsDriverId, driverMonthlyStatsLoading, driverMonthlyStatsMonth,
            driverNoticeForm, driverNotices, driverNoticeTranslating, showDriverNoticeModal, editingDriverNotice,
            driverOnboardingLoading, driverTrainingConfig, selectedDriverOnboarding,
            adminMobileOrderDetail, editingDriverAccount, filteredOrders,
            handleApproveUser, handleBatchDeleteUsers, handleBulkDelete, handleCreateDriverAccount,
            handleBulkMarkPaid: (user?.is_master || user?.permissions?.includes("finance.manage")) ? handleBulkMarkPaid : undefined, isMarkingPaid,
            handleDeleteBillingDocument, handleDeleteDispatcher, handleDeleteDriverAccount, handleDeleteOrder, handleDuplicateOrder,
            handleDriverMonthlyStatsDispatcherChange, handleDriverMonthlyStatsDriverChange,
            handleDriverMonthlyStatsMonthChange, handleExportBilling, handleExportDriverMonthlyStats,
            handleSubmitDriverMonthlySettlements,
            handleUpdateDriverMonthlyDispatchPrice,
            handleReviewDriverOnboarding, handleSaveDriverTrainingConfig,
            handleEditDriverNotice, handleSaveDriverNotice, handleTranslateDriverNotice,
            handleApplyCustomerOvertimeCharge, handleMarkOvertimeHandled, handleMigrateDriverOrders, handleNotifyAssignedDriver, handlePutOrderIntoPool,
            handleWithdrawOrderFromPool, handleCreateDriverComplaint, handleResolveDriverIssue,
            handleOpenRecentOrder, handleResetDriverPassword, handleRestoreOrder, handleSearchKeyDown,
            handleSearchOrders, handleToggleDispatcherActive, handleToggleDriverActive,
            handleToggleDriverInternalDispatchOverview,
            handleToggleDriverPinned,
            handleToggleDriverRevenueStats,
            handleUnlockOrder, handleUpdateDriverAccount, handleUpdateUserChatSettings,
            handleUpdateUserRetailMemberBindingSettings,
            handleUpdateUserStatus, hideCancelled,
            hideCompleted, isOrderIncomplete, isSearchMode, isSearching, layoutConfig, layoutLoading,
            loading, monthFilter, serviceDateRange, onServiceDateRangeChange: setServiceDateRange, monthlyTrend, openDriverAssignModal, orderRangeHelpText: ADMIN_ORDER_RANGE_HELP_TEXT,
            orderStatusFilter, orders,
            communicationOrderId, pendingUsers, recentOrders, searchKeyword, searchMessage, searchResults,
                selectedCustomerStats, selectedOrderIds, selectedUserIds, selectAll, setBillingFilters,
                setDriverAccountForm, setDriverNoticeForm, setDriverTrainingConfig, setShowDriverNoticeModal, setShowDriverTrainingConfig, showDeletedUsers, showDriverTrainingConfig, sortByDate, sortDateDirection: dateSortDirection, statsLoading, driverTimeOffByDate,
                    mobileDispatchSelectedDriverIds, mobileDispatchSelectionInitialized,
                    mobileDispatchExtraSchedules, mobileDispatchExtraSchedulesLoading, mobileDispatchExtraSchedulesError,
                    mobileOrderInitialQuickView,
                    unlinkedCustomerOnly,
                  }}
              {...{
            onAddAreaToRow: addAreaToRow, onAddLayoutRow: addLayoutRow,
            onAssignDriver: openDriverAssignModal, onClearSearch: clearSearch,
            onCloseCustomerStats: closeCustomerStats, onCustomerFilterChange: setCustomerFilter,
            onFetchDriverSchedule: fetchDriverSchedule, onHideCancelledChange: setHideCancelled,
            onFetchDriverMonthlyStats: fetchDriverMonthlyStats,
            onFetchDriverOnboarding: fetchDriverOnboarding,
            fetchDriverNotices,
            fetchDriverTrainingConfig,
            onFetchBillingDocuments: fetchBillingDocuments,
            onFetchReceiptDocuments: fetchReceiptDocuments,
            onHideCompletedChange: setHideCompleted, onMonthFilterChange: setMonthFilter,
            onMoveAreaDown: moveAreaDown, onMoveAreaUp: moveAreaUp,
            onOpenAdminOrderForm: isAdminAppMode ? openAdminOrderAppPage : openAdminOrderForm,
            onOpenCreateDriver: openCreateDriverForm, onOpenCreateUserForm: () => setShowCreateUserForm(true),
            onOpenCustomerPricing: openCustomerPricing, onOpenDispatcher: openCreateDispatcherForm,
            onOpenEditDispatcher: openEditDispatcherForm, onOpenEditDriver: openEditDriverForm,
            onOpenQuoteForm: openQuoteForm, onOpenResetPassword: openResetPasswordForm,
            onOpenUserProfile: openUserProfileForm,
            onOpenTripCommunication: openTripCommunication,
            onOpenExceptionOrder: openOperationsOrder,
            onSelectOrderGroup: selectOrderGroup,
            onSettleOrder: settlementProps.openCreateSettlement,
            onOpenOrderDetail: isAdminAppMode ? openAdminMobileOrderDetail : handleEditOrder,
            onAdminOrderCreated: () => {
              fetchOrders();
              refreshStatsAfterOrderChange();
              setActiveTab("orders");
            },
            onSelectAdminTab: setActiveTab,
            onLogout,
            onConfirmRetailBooking: confirmRetailBooking, onCancelRetailBooking: cancelRetailBooking,
                onGeneratePaymentPage: generateBookingPaymentPage,
                onRefreshData: refreshAdminMobileData,
                onRefreshMobileDispatchData: refreshAdminMobileDispatchData,
                onFetchMobileDispatchDriverHistory: fetchMobileDispatchDriverHistory,
                    onMobileDispatchSelectedDriverIdsChange: updateMobileDispatchSelectedDriverIds,
                onMobileDispatchSelectionInitializedChange: setMobileDispatchSelectionInitialized,
                onMobileOrderInitialQuickViewChange: setMobileOrderInitialQuickView,
                onOpenDriverReportOrders: openAdminMobileDriverReports,
                onOrderStatusFilterChange: setOrderStatusFilter, onDownloadSignboard: handleDownloadSignboard,
            onRemoveAreaFromRow: removeAreaFromRow,
            onRemoveLayoutRow: removeLayoutRow, onResetFilters: resetFilters,
            onSaveLayoutConfig: saveLayoutConfig, onSearchKeywordChange: setSearchKeyword,
            onShowDeletedUsersChange: setShowDeletedUsers, onCycleDateSort: cycleDateSort,
            onToggleUnlinkedCustomerOnly: toggleUnlinkedCustomerOnly,
            onCloseDriverOnboarding: () => setSelectedDriverOnboarding(null),
            onToggleAllUsers: toggleAllUsers, onToggleSelectAll: toggleSelectAll,
            onToggleSelectOrder: toggleSelectOrder, onToggleUserSelection: toggleUserSelection,
            onBillingDocumentFiltersChange: handleBillingDocumentFiltersChange,
            onReceiptDocumentFiltersChange: handleReceiptDocumentFiltersChange,
            onCreateManualInvoice: handleCreateManualInvoice,
            onCreateManualReceipt: handleCreateManualReceipt,
            onCreateCombinedOrderReceipt: handleCreateCombinedOrderReceipt,
            onCreateSingleOrderReceipts: handleCreateSingleOrderReceipts,
            onSearchReceiptOrders: handleSearchReceiptOrders,
            onDownloadBillingDocument: handleDownloadBillingDocument,
            onExportBillingDocuments: handleExportBillingDocuments,
            onRestoreBillingDocument: handleRestoreBillingDocument,
          }}
          pricingProps={{
            areaNameMap, areas, charterPricing, extraHoursConfig, fetchPricingConfig,
            importPresetData, initializeAreas, nightSurcharges, overtimeRates, pricingTab,
            selectedAreas, setEditingItem, setExtraHoursConfig, setPricingTab, setShowAreaForm,
            setShowBatchAreaForm, setShowSurchargeForm, toggleAllAreas, toggleAreaSelection,
            transferPricing,
          }}
          uniqueCustomers={getUniqueCustomers()}
          viewHelpers={{
            closeDriverAccountForm, customerFilter, fetchCustomerStats, formatDestinationDisplay,
            formatPickupDisplay, getCharterTimeStatus, getOrderTimeCategory, getStatusBadge,
            getTimeCategoryBadge, getVehicleTypeName, isMissingDriverOrder, groupOrdersByDate, handleEditOrder,
            showDriverAccountForm,
          }}
        />
      </div>

      <SettlementFormModal settlementProps={settlementProps} />

      {isAdminAppMode && (
        <div className={`admin-mobile-sync-banner ${loading ? "is-visible" : ""}`} aria-live="polite">
          <RefreshCw className="h-4 w-4" />
          <span>同步中</span>
        </div>
      )}

      {isAdminAppMode && (
        <nav className="admin-mobile-tabbar" aria-label="管理员 App 导航">
          {(user?.is_master||user?.permissions?.includes("orders.view"))&&<>
          <button type="button" className={activeTab === "mobileHome" ? "is-active" : ""} onClick={() => setActiveTab("mobileHome")}>
            <Home className="h-5 w-5" />
            <span>首页</span>
          </button>
          <button type="button" className={activeTab === "orders" ? "is-active" : ""} onClick={() => setActiveTab("orders")}>
            <CalendarDays className="h-5 w-5" />
            <span>订单</span>
          </button>
          </>}
          {(user?.is_master||user?.permissions?.includes("orders.manage"))&&
          <button
            type="button"
            aria-label="创建订单"
            className={`admin-mobile-tabbar-create ${activeTab === "mobileCreate" ? "is-active" : ""}`}
            onClick={openAdminOrderAppPage}
          >
            <Plus className="h-6 w-6" />
          </button>
          }
          {(user?.is_master||user?.permissions?.includes("dispatch.manage"))&&<>
          <button type="button" className={activeTab === "drivers" ? "is-active" : ""} onClick={() => setActiveTab("drivers")}>
            <UserRound className="h-5 w-5" />
            <span>司机</span>
          </button>
          <button type="button" className={activeTab === "mobileDispatch" ? "is-active" : ""} onClick={() => setActiveTab("mobileDispatch")}>
            <Route className="h-5 w-5" />
            <span>调度</span>
          </button>
          </>}
        </nav>
      )}

      {isAdminAppMode && adminAppNotice && (
        <div className="admin-mobile-toast" role="status" aria-live="polite">
          {adminAppNotice}
        </div>
      )}

      {hasOpenAdminModal && (
      <Suspense fallback={null}>
      <AdminDashboardModals
        {...{
          allUsers, areas, closeCustomerPricing, closeDriverSchedule, closeOrderEditForm,
          closeDriverComplaintForm, closeOrderPoolForm,
          closeQuoteForm, closeResetPasswordForm, dispatcherForm, driverAccounts,
          driverAccountsError, driverAccountsLoading, fetchDriverAccounts,
          driverComplaintError, driverComplaintForm, driverComplaintOptions, driverComplaintOptionsLoading, driverComplaintSubmitting,
          closeUserProfileForm,
          driverExtraSchedules, driverForm, driverScheduleLoading, driverScheduleOrders, duplicateOrderPrefill,
          editingDispatcher, editingOrder, formatDestinationDisplay, formatPickupDisplay,
          getAvailableVehicleTypes, getVehicleTypeName, handleAssignDriverToOrder,
          handleCancelExtraSchedule, handleCreateQuote, handleDeleteDriver,
          handleRecalculateLatestPrice, handleResetPassword, handleUpdateUserProfile, handleSaveDriver,
          handleDriverComplaintIssueToggle, handleSubmitDriverComplaintForm, handleSubmitOrderPoolForm,
          handleToggleFavoriteDriver, handleUnassignDriver, handleUpdateOrder, handleNotifyAssignedDriver, handleResolveDriverIssue,
          hideCompletedDriverOrders, isKansaiRegion, loading, newPasswordForm, newUserForm,
          quoteForm, savedDrivers, selectedCustomerForPricing, selectedDriverForSchedule,
          selectedOrderForComplaint, selectedOrderForDriver, selectedOrderForPool, selectedOrderForQuote, selectedUserForAction,
          setNewPasswordForm, setNewUserForm, setUserProfileForm, showAdminOrderForm, showCreateUserForm,
          showCustomerPricingForm, showDispatcherForm, showDriverAssignModal,
          showDriverComplaintForm, showDriverSchedule, showOrderEditForm, showOrderPoolForm,
          showQuoteForm, showResetPasswordForm,
          showUserProfileForm, userProfileForm, driverTimeOffByDate, orderPoolForm, orderPoolSubmitting,
          orderPoolError, paymentCopyPanel,
        }}
        onAdminOrderCreated={() => {
          fetchOrders();
          setDuplicateOrderPrefill(null);
          setShowAdminOrderForm(false);
        }}
        onCloseAdminOrderForm={() => { setDuplicateOrderPrefill(null); setShowAdminOrderForm(false); }}
        onCloseCreateUserForm={() => setShowCreateUserForm(false)}
        onCloseDispatcherForm={closeDispatcherForm}
        onCloseDriverAssignModal={() => setShowDriverAssignModal(false)}
        onClosePaymentCopyPanel={closePaymentCopyPanel}
        onCreateUser={handleCreateUser}
        onDispatcherFormChange={setDispatcherForm}
        onDriverFormChange={setDriverForm}
        setDriverComplaintForm={setDriverComplaintForm}
        setOrderPoolForm={setOrderPoolForm}
        onHideCompletedDriverOrdersChange={setHideCompletedDriverOrders}
            onNotifyAssignedDriver={handleNotifyAssignedDriver}
            onPutOrderIntoPool={handlePutOrderIntoPool}
            onWithdrawOrderFromPool={handleWithdrawOrderFromPool}
            onCreateDriverComplaint={handleCreateDriverComplaint}
            onResolveDriverIssue={handleResolveDriverIssue}
        onOrderCreated={fetchAllUsers}
        onQuoteFormChange={setQuoteForm}
        onRetryCopyPaymentMessage={retryCopyPaymentMessage}
        onOpenPaymentMessageWhatsApp={openPaymentMessageWhatsApp}
        onSharePaymentMessage={sharePaymentMessage}
        onUpdateDispatcher={editingDispatcher ? handleUpdateDispatcher : handleCreateDispatcher}
        orderEdit={{
          adminEditExtraServices, adminEditOtherServiceDesc, adminEditParkingFee, apiCalculatedPrice, apiPriceLoading,
          adminEditExtendedWaitingQuantity,
          getAdminCalculatedPrice, getAdminChildSeatPrice, getAdminExtraServiceTotal,
          getAdminVehiclePrice, handleViewDriverScheduleOrder, orderEditForm,
          handleRequestSquareRefund,
          setAdminEditExtraServices, setAdminEditOtherServiceDesc, setAdminEditParkingFee, setOrderEditForm,
          setAdminEditExtendedWaitingQuantity,
        }}
      />
      </Suspense>
      )}
    </div>
  );
}
